//#region IMPORT
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
/** Services Import */
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { useDispatch, useSelector } from 'react-redux';
/** Components */
import { SER_BASIC_01 } from './index';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { showLoading, closeLoading } from '../../../../Reducer/LoadingReducer';
import { useSave } from '../../../../hooks/useSaveN';
import {
  useSearch1,
  useSearch2,
  useSave1,
  useSave2,
  useSave3,
} from './SER_BASIC_01_QUERY';
import { SER_BASIC_01_Schema } from './SER_BASIC_01_Schema';
import { useSearchFormDetail, useSaveForm, useSearchFormList, usePublishForm } from '../SER_BASIC/SER_BASIC_FORM_QUERY';
import {
  PickActiveFormRow,
  ParseFormDataResponse,
} from '../SER_BASIC/SER_BASIC_FORM_Storage';
import {
  DownloadSeriousAccidentExcelAll,
  DownloadSeriousAccidentWorkbook,
  SanitizeExcelSheetName,
  WriteSeriousAccidentStepToWorksheet,
} from './SER_BASIC_01_Excel';
import { CalcCategoryTotalForTemplate } from '../serBasicScore';
//#endregion

// 화면 MENU_ID
const SCREEN_MENU_ID = 'SeriousAccidentAct1';

//#region 상수
const DEFAULT_STEPS = [
  { label: '경영자 안전보건경영활동' },
  { label: '위험성 평가' },
  { label: '안전보건교육' },
  { label: '근로자 참여' },
  { label: '중대재해 대응조치' },
  { label: '도급.용역.위탁 안전보건확보' },
  { label: '평가 및 개선' },
  { label: '안전문화 및 안전의식' },
  { label: '가점 사항' },
];

const CreateTempCode = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/** 대분류(카테고리)별 최대 배점 */
const CATEGORY_MAX_SCORE = 100;

/** 평가지표 배점을 착안사항 개수로 균등 분배한 1건당 점수 */
const GetCheckpointShare = (indicator, checkpoints) => {
  const count = checkpoints?.length || 1;
  return Number(indicator?.val2 || 0) / count;
};

/** 지표·착안사항 기준 취득점수·페이지별 점수 재계산 */
const ComputeScores = (list, list2, stepCount) => {
  const updatedList = (list || []).map((indicator) => {
    const checkpoints = (list2 || []).filter((c) => c.groupCd === indicator.code);
    const share = GetCheckpointShare(indicator, checkpoints);
    let totalPo = 0;
    checkpoints.forEach((cp) => {
      if (cp.check === 'Y') totalPo += share;
    });
    return {
      ...indicator,
      val3: totalPo.toFixed(2),
    };
  });

  const pagePts = {};
  for (let i = 1; i <= stepCount; i += 1) {
    pagePts[`no${i}`] = 0;
  }
  let total = 0;
  updatedList.forEach((ind) => {
    const v = Number(ind.val3 || 0);
    total += v;
    const key = `no${ind.val1}`;
    if (pagePts[key] !== undefined) pagePts[key] += v;
  });

  return {
    updatedList,
    pagePts,
    total: Number(total.toFixed(2)),
  };
};
//#endregion

function SER_BASIC_01F() {
  //#region 변수
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, lanInfo, custInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.SearchInfo);

  const dispatch = useDispatch();
  const custCd =
    location?.state?.custCd ||
    searchInfo?.custCd ||
    userInfo?.custCd ||
    '';
  const screenMode = location?.state?.screenMode || 'evaluate';
  const isTemplateMode = screenMode === 'template';
  const readOnlyStructure = screenMode === 'evaluate';
  const formCodeRef = useRef(location?.state?.formCode || location?.state?.versionId || '');
  const versionIdRef = useRef(
    String(location?.state?.versionId ?? location?.state?.formCode ?? '')
  );
  const formVerRef = useRef(location?.state?.formVer || '');
  const formNameRef = useRef(location?.state?.formName || '');
  const formStatusRef = useRef(location?.state?.status || 'DRAFT');
  const templateReadOnlyRef = useRef(
    location?.state?.readonlyYn === 'Y' ||
      (location?.state?.screenMode === 'template' &&
        String(location?.state?.status || '').toUpperCase() !== 'DRAFT')
  );
  const initialFormDataRef = useRef(location?.state?.initialFormData || null);
  const isNewFormRef = useRef(Boolean(location?.state?.isNewForm));
  const useActiveFormRef = useRef(Boolean(location?.state?.useActiveForm));

  /** 엑셀용 사업장명 (라우트 state → 검색조건 custList → BaseInfo custInfo) */
  const custNmForExcel = useMemo(() => {
    const fromNav = String(location?.state?.custNm ?? '').trim();
    if (fromNav) return fromNav;
    const cd = String(custCd || searchInfo?.custCd || '').trim();
    if (!cd) return '';
    const list = Array.isArray(searchInfo?.custList) ? searchInfo.custList : [];
    const fromList = list.find((c) => String(c.value) === cd);
    if (fromList?.name) return String(fromList.name).trim();
    const fromInfo = Array.isArray(custInfo)
      ? custInfo.find((c) => String(c.custCd) === cd)
      : null;
    if (fromInfo?.custNm) return String(fromInfo.custNm).trim();
    return '';
  }, [
    location?.state?.custNm,
    custCd,
    searchInfo?.custCd,
    searchInfo?.custList,
    custInfo,
  ]);
  const sdCodeRef = useRef(location?.state?.data?.sdCode || '');
  const [dateValue, setDateValue] = useState({
    evaDate: location?.state?.data?.evaDate
      ? location.state.data.evaDate
      : dayjs().format('YYYY-MM-DD'),
    evaName: location?.state?.data?.evaName
      ? location.state.data.evaName
      : userInfo.userNm,
  }); //조회조건 평가일자, 평가자
  const [codeList, setCodeList] = useState([]); //공통코드 데이터
  const [arrayList, setArrayList] = useState([]); //테이블 평가 지표 데이터
  const [arrayList2, setArrayList2] = useState([]); //테이블 착안사항 평가(적정) 데이터
  // 상세(착안사항) 원본 스냅샷: 조회 시 DB 답안 merge용
  const arrayList2OriginRef = useRef([]);
  const structureRef = useRef({ steps: [], arrayList: [], arrayList2: [] });

  //페이지별 취득점수 총합
  const pagePoint = React.useRef({
    no1: 0,
    no2: 0,
    no3: 0,
    no4: 0,
    no5: 0,
    no6: 0,
    no7: 0,
    no8: 0,
    no9: 0,
  });

  const [totalPoint, setTotalPoint] = useState(0); //총취득점수

  const [steps, setSteps] = useState(() =>
    DEFAULT_STEPS.map((s, i) => ({
      id: `cat-${i + 1}`,
      label: s.label,
    }))
  );
  const [activeStep, setActiveStep] = React.useState(0); //페이지 이동 변수
  const maxSteps = steps.length;
  //#endregion

  const SyncScores = (list, list2, stepCount = steps.length) => {
    const { updatedList, pagePts, total } = ComputeScores(list, list2, stepCount);
    pagePoint.current = pagePts;
    setArrayList(updatedList);
    setArrayList2(list2);
    setTotalPoint(total);
    structureRef.current = {
      steps,
      arrayList: updatedList,
      arrayList2: list2,
    };
  };

  // QUERY: 조회/저장 훅 연결
  const search1 = useSearch1();
  const search2 = useSearch2();
  const searchFormDetail = useSearchFormDetail();
  const searchFormList = useSearchFormList();
  const { runAsync: SaveFormAsync } = useSaveForm();
  const { runAsync: PublishFormAsync } = usePublishForm();
  const { runAsync: SaveMasterAsync } = useSave1();
  const { runAsync: SaveDetailAsync } = useSave2();
  const { runAsync: SavePointAsync } = useSave3();

  const initOnceRef = useRef(false);

  const ApplyTemplateData = (formData, options = {}) => {
    const nextSteps =
      formData?.steps?.length > 0
        ? formData.steps
        : DEFAULT_STEPS.map((s, i) => ({
            id: `cat-${i + 1}`,
            label: s.label,
          }));
    let nextList = formData?.arrayList || [];
    let nextList2 = formData?.arrayList2 || [];
    if (options.resetChecks) {
      nextList2 = nextList2.map((row) => ({ ...row, check: 'N' }));
    }
    setSteps(nextSteps);
    SyncScores(nextList, nextList2, nextSteps.length);
    arrayList2OriginRef.current = (nextList2 || []).map((r) => ({ ...r }));
    setActiveStep(0);
  };

  const ParseFormDataPayload = (row) => ParseFormDataResponse(row);

  /** 선택 양식 구조 로드 (GetSerBasicR3) */
  const LoadTemplateLogic = async (versionId) => {
    const vid = String(versionId || versionIdRef.current || formCodeRef.current || '').trim();
    if (!vid) {
      InitEmptyTemplateLogic();
      return;
    }

    versionIdRef.current = vid;
    formCodeRef.current = vid;

    let formData = initialFormDataRef.current;
    initialFormDataRef.current = null;

    if (!formData) {
      try {
        await searchFormDetail.runAsync(
          { custCd, versionId: vid, formCode: vid, userInfo },
          {
            onSuccess: (rows) => {
              const row = Array.isArray(rows) ? rows?.[0] : rows;
              formData = ParseFormDataPayload(row);
              if (row?.versionNm) formNameRef.current = row.versionNm;
              if (row?.versionNo) formVerRef.current = String(row.versionNo);
              if (row?.status) formStatusRef.current = row.status;
            },
          }
        );
      } catch (e) {
        console.log(e);
      }
    }

    if (formData?.steps || formData?.arrayList) {
      ApplyTemplateData(formData, {
        resetChecks: readOnlyStructure && !sdCodeRef.current,
      });
      return;
    }

    dispatch(
      showSnackbar({
        message: '양식 데이터를 불러오지 못했습니다.',
        type: 'warning',
      })
    );
    InitEmptyTemplateLogic();
  };

  const InitEmptyTemplateLogic = () => {
    const nextSteps = DEFAULT_STEPS.map((s) => ({
      id: CreateTempCode('cat'),
      label: s.label,
    }));
    setSteps(nextSteps);
    SyncScores([], [], nextSteps.length);
    arrayList2OriginRef.current = [];
    setActiveStep(0);
    dispatch(
      showSnackbar({
        message:
          '빈 양식으로 시작합니다. 대분류별 평가지표·착안사항·배점을 입력해 주세요.',
        type: 'info',
      })
    );
  };

  /** 백엔드 기준 ACTIVE 평가표 로드 */
  const LoadActiveFormLogic = async () => {
    let listRows = [];
    try {
      await searchFormList.runAsync(
        { custCd, userInfo },
        {
          onSuccess: (data) => {
            listRows = Array.isArray(data) ? data : [];
          },
        }
      );
    } catch (e) {
      console.log(e);
    }

    const active = PickActiveFormRow(listRows);
    if (!active?.versionId && !active?.formCode) {
      dispatch(
        showSnackbar({
          message:
            '배포된 평가 양식(ACTIVE)이 없습니다. 평가표 관리에서 양식을 배포한 후 평가를 진행해 주세요.',
          type: 'warning',
        })
      );
      if (!isTemplateMode) {
        navigate('/SeriousAccidentAct');
      } else {
        InitEmptyTemplateLogic();
      }
      return;
    }

    versionIdRef.current = String(active.versionId ?? active.formCode);
    formCodeRef.current = versionIdRef.current;
    formVerRef.current = String(active.versionNo ?? active.formVer ?? '');
    formNameRef.current = active.versionNm ?? active.formName ?? '';
    formStatusRef.current = active.status ?? 'ACTIVE';
    await LoadTemplateLogic(versionIdRef.current);
  };

  /** 기존 평가 답안을 양식 구조에 병합 */
  const MergeEvaluationAnswersLogic = (detailRows = []) => {
    const baseList =
      structureRef.current.arrayList?.length > 0
        ? structureRef.current.arrayList
        : arrayList;
    const baseList2 =
      structureRef.current.arrayList2?.length > 0
        ? structureRef.current.arrayList2
        : arrayList2;
    const checkMap = new Map(
      detailRows.map((r) => [r.seriousDisaster, r.checkYn ?? 'N'])
    );
    const nextList2 = baseList2.map((row) => ({
      ...row,
      check: checkMap.has(row.code) ? checkMap.get(row.code) : row.check || 'N',
    }));
    SyncScores(baseList, nextList2, steps.length);
    arrayList2OriginRef.current = (nextList2 || []).map((r) => ({ ...r }));
  };

  // 초기 1회 실행 의도 (deps 고정)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initOnceRef.current) return;
    initOnceRef.current = true;
    (async () => {
      try {
        if (isTemplateMode) {
          await LoadTemplateLogic(versionIdRef.current || formCodeRef.current);
          return;
        }

        if (versionIdRef.current || formCodeRef.current) {
          await LoadTemplateLogic(versionIdRef.current || formCodeRef.current);
        } else if (useActiveFormRef.current) {
          await LoadActiveFormLogic();
        } else {
          await SearchCode('0');
        }

        if (sdCodeRef.current) {
          SearchLogic1(sdCodeRef.current);
        } else if (!formCodeRef.current && !useActiveFormRef.current) {
          setTotalPoint(0);
          setActiveStep(0);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  //#region 조회
  // 마스터 조회(sdCode 기준)
  const SearchLogic1 = async (sdCode) => {
    if (!sdCode) return;

    search1.run(
      { sdCode },
      {
        onSuccess: (rows) => {
          const row = rows?.[0];
          if (!row) return;

          // 화면 헤더 반영
          setDateValue((prev) => ({
            ...prev,
            evaDate: row.evaDate || prev.evaDate,
            evaName: row.evaName || prev.evaName,
          }));
          setTotalPoint(row.evaPoint || 0);
          sdCodeRef.current = row.sdCode || sdCode;
          if (row.versionId) {
            versionIdRef.current = String(row.versionId);
            formCodeRef.current = versionIdRef.current;
          } else if (row.formCode) {
            versionIdRef.current = String(row.formCode);
            formCodeRef.current = versionIdRef.current;
          }
          if (row.versionNo || row.formVer) {
            formVerRef.current = String(row.versionNo ?? row.formVer);
          }
          if (row.versionNm) formNameRef.current = row.versionNm;

          SearchLogic2(sdCodeRef.current, {
            mergeOnly: readOnlyStructure && !!formCodeRef.current,
          });
          setActiveStep(0);
        },
      }
    );
  };
  //조회버튼(_value1 : 중대재해처벌법 코드 )
  // 상세 조회(sdCode 기준)
  const SearchLogic2 = async (sdCode, options = {}) => {
    if (!sdCode) return;

    // 페이지별 점수 초기화(재조회 시 누적 방지)
    Object.keys(pagePoint.current).forEach((key) => {
      pagePoint.current[key] = 0;
    });

    search2.run(
      { sdCode },
      {
        onSuccess: (data) => {
          if (!data?.length) return;

          if (options.mergeOnly) {
            MergeEvaluationAnswersLogic(data);
            return;
          }

          const indicatorMap = data.reduce((acc, item) => {
            const key = item.groupCd;
            if (!key || acc[key]) return acc;
            acc[key] = {
              codeNm: item.indicatorNm ?? item.codeNm,
              val1: item.val1,
              val2: item.val2,
              code: item.groupCd,
              val3: 0,
            };
            return acc;
          }, {});

          const groupedArray = Object.values(indicatorMap);
          setArrayList(groupedArray);

          let changeData = [];
          for (let i = 0; i < groupedArray.length; i++) {
            const indicatorCode = groupedArray[i].code;
            const indicatorRows = data.filter((e) => e.groupCd === indicatorCode);
            const rowLen = indicatorRows.length || 1;
            const quesPo = Number(groupedArray[i].val2 || 0) / rowLen;
            let totalPo = 0;

            indicatorRows.forEach((_item2) => {
              totalPo = _item2.checkYn === 'Y' ? totalPo + quesPo : totalPo;
              changeData.push({
                code: _item2.seriousDisaster,
                groupCd: _item2.groupCd,
                codeNm: _item2.seriousDisasterNm,
                check: _item2.checkYn || 'N',
                val1: _item2.val1,
              });
            });
            groupedArray[i].val3 = totalPo.toFixed(2);
            ChangePagePoint(groupedArray[i].val1, totalPo, 'add');
          }

          setArrayList2(changeData);
          arrayList2OriginRef.current = (changeData || []).map((r) => ({
            ...r,
          }));
          SearchCode('1');
        },
      }
    );
  };
  // 조회(_value1 : "0"이면 데이터없을경우 "1"이면 데이터있을경우)
  const SearchCode = async (_value1) => {
    try {
      const result = await Commons.Axios(
        'post',
        Global.base.useUrl + Global.urls.Utility.getCodeInfo,
        {}
      );

      const apiRes = result?.data;
      const isWrapped =
        apiRes && typeof apiRes === 'object' && 'data' in apiRes;

      // 1) 에러 처리(Wrapper 응답 우선)
      if (isWrapped && apiRes?.isSuccess === false) {
        dispatch(
          showSnackbar({
            message: apiRes?.message || '코드 정보를 불러오지 못했습니다.',
            type: apiRes?.severity || 'error',
          })
        );
        setCodeList([]);
        return false;
      }

      // 2) rows 추출(Wrapper/Legacy 모두 대응)
      const rows = Array.isArray(apiRes)
        ? apiRes
        : Array.isArray(apiRes?.data)
          ? apiRes.data
          : [];

      if (rows.length === 0) {
        setCodeList([]);
        return;
      }

      // Legacy 스타일(type: 'Error') 방어
      if (rows?.[0]?.type === 'Error') {
        dispatch(showSnackbar({ message: rows?.[0]?.message, type: 'error' }));
        setCodeList([]);
        return false;
      }

      // 3) 필드명 표준화(GetCodeInfo: GroupCd/Code/... → 기존 화면: groupCd/code/...)
      const normalized = rows.map((r) => ({
        groupCd: r?.groupCd ?? r?.GroupCd ?? '',
        code: r?.code ?? r?.Code ?? '',
        codeNm: r?.codeNm ?? r?.CodeNm ?? '',
        val1: r?.val1 ?? r?.Val1 ?? '',
        val2: r?.val2 ?? r?.Val2 ?? '',
        val3: r?.val3 ?? r?.Val3 ?? '',
        ref1: r?.ref1 ?? r?.Ref1 ?? '',
        ref2: r?.ref2 ?? r?.Ref2 ?? '',
        ref3: r?.ref3 ?? r?.Ref3 ?? '',
      }));

      setCodeList(normalized);

      // NOTE: 화면은 템플릿 전체 항목 + DB 답안 merge 로 구성한다.
      const checkMap = new Map(
        (arrayList2OriginRef.current || []).map((r) => [r.code, r.check || '0'])
      );
      const defaultCheck = 'N'; // 기존 UX: 신규 평가는 미흡(N) 기본 선택

      const seriousData = normalized.filter((e) => e.groupCd === 'SERIOUS_DISASTER');
      const nextArrayList = [];
      const nextArrayList2 = [];

      seriousData.forEach((header) => {
        const details = normalized.filter((e) => e.groupCd === header.code);
        const rowLen = details.length || 1;
        const quesPo = Number(header.val2 || 0) / rowLen;

        let totalPo = 0;
        details.forEach((d) => {
          const mergedCheck =
            (_value1 === '1' ? checkMap.get(d.code) : null) ?? defaultCheck;

          if (mergedCheck === 'Y') {
            totalPo += quesPo;
          }

          nextArrayList2.push({
            code: d.code,
            groupCd: d.groupCd,
            codeNm: d.codeNm,
            check: mergedCheck,
            val1: d.val1,
          });
        });

        nextArrayList.push({
          code: header.code,
          codeNm: header.codeNm,
          val1: header.val1,
          val2: header.val2,
          val3: totalPo.toFixed(2),
        });
      });

      const { updatedList, pagePts, total } = ComputeScores(
        nextArrayList,
        nextArrayList2,
        steps.length
      );
      pagePoint.current = pagePts;
      setTotalPoint(total);
      setArrayList(updatedList);
      setArrayList2(nextArrayList2);
      // 원본 스냅샷도 "전체 항목" 기준으로 유지(조회 merge에 사용)
      arrayList2OriginRef.current = (nextArrayList2 || []).map((r) => ({ ...r }));
    } catch (e) {
      console.log(e);
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    } finally {
    }
  };
  //#endregion
  //#region 저장
  /** DRAFT 양식 저장 코어 */
  const SaveTemplateCore = async () => {
    const versionId = String(versionIdRef.current || formCodeRef.current || '').trim();
    if (!versionId) {
      throw new Error('저장할 DRAFT 버전 정보가 없습니다. 목록에서 다시 열어주세요.');
    }
    if (!custCd) {
      throw new Error('사업장 정보가 없습니다. 목록 화면에서 다시 진입해 주세요.');
    }

    const formName =
      formNameRef.current?.trim() ||
      `중대재해 평가 양식 v${formVerRef.current || dayjs().format('YYYYMMDD')}`;

    const categoryTotal = CalcCategoryTotalForTemplate(
      steps,
      arrayList,
      CATEGORY_MAX_SCORE
    );

    const result = await SaveFormAsync({
      rows: [
        {
          versionId,
          formCode: versionId,
          versionNm: formName,
          formName,
          totalScore: categoryTotal || 800,
          formData: { steps, arrayList, arrayList2 },
        },
      ],
      context: { custCd, userInfo, search: searchInfo },
    });

    const isSuccess = result?.isSuccess ?? result?.IsSuccess;
    if (!isSuccess) {
      throw new Error(result?.message || result?.Message || '양식 저장 실패');
    }

    formNameRef.current = formName;
    formStatusRef.current = 'DRAFT';
    return formName;
  };

  /** DRAFT 양식 저장 (SaveSerBasicU2) */
  const SaveTemplateLogic = async () => {
    if (templateReadOnlyRef.current) {
      dispatch(
        showSnackbar({
          message: '읽기 전용 양식은 저장할 수 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      dispatch(showLoading());
      const formName = await SaveTemplateCore();
      dispatch(
        showSnackbar({
          message: `양식이 저장되었습니다. (${formName})`,
          type: 'success',
        })
      );
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '양식 저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  /** DRAFT → ACTIVE 배포 (SaveSerBasicU3) */
  const PublishTemplateLogic = async () => {
    const versionId = String(versionIdRef.current || formCodeRef.current || '').trim();
    if (!versionId) {
      dispatch(
        showSnackbar({
          message: '배포할 DRAFT 버전 정보가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    try {
      dispatch(showLoading());
      await SaveTemplateCore();
      const result = await PublishFormAsync({ custCd, versionId, userInfo });
      const isSuccess = result?.isSuccess ?? result?.IsSuccess;
      if (!isSuccess) {
        throw new Error(result?.message || result?.Message || '배포 실패');
      }

      formStatusRef.current = 'ACTIVE';
      templateReadOnlyRef.current = true;

      dispatch(
        showSnackbar({
          message: '평가표가 배포되었습니다. (ACTIVE)',
          type: 'success',
        })
      );
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '배포 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  //저장버튼 클릭 시 함수()
  const { run: SaveAll } = useSave({
    menuId: SCREEN_MENU_ID,
    schema: SER_BASIC_01_Schema,
    getChangedRows: () => [
      {
        sdCode: sdCodeRef.current || '',
        custCd,
        evaDate: dayjs(dateValue.evaDate).format('YYYY-MM-DD'),
        evaName: dateValue.evaName,
        evaPoint: Math.round(Number(totalPoint) || 0),
      },
    ],
    requestSave: async (rows) => {
      const header = rows?.[0] || {};
      const wasNew = !sdCodeRef.current;

      // 1) 마스터 저장
      const res1 = await SaveMasterAsync([
        {
          ...header,
          versionId: versionIdRef.current || formCodeRef.current || '',
        },
      ], { userInfo });
      const isSuccess1 = res1?.isSuccess ?? res1?.IsSuccess;
      if (isSuccess1 === false) {
        throw new Error(
          res1?.message || res1?.Message || '저장 중 오류가 발생했습니다.'
        );
      }
      const d1 = Array.isArray(res1?.data) ? res1.data : [];
      const newSdCode =
        d1?.[0]?.sdCode ||
        d1?.[0]?.SdCode ||
        header.sdCode ||
        sdCodeRef.current;
      sdCodeRef.current = newSdCode;

      // 2) 상세 저장(착안사항 평가) — 전체 행
      const allDetailRows = (arrayList2 || []).map((r) => ({
        sdCode: newSdCode,
        seriousDisaster: r.code,
        checkYn: r.check || '0',
      }));

      const res2 =
        allDetailRows.length > 0
          ? await SaveDetailAsync(allDetailRows, { userInfo })
          : { severity: 'success', data: [] };
      const d2 = Array.isArray(res2?.data) ? res2.data : [];

      // 3) 페이지별 점수 저장 — 전체 행
      const evaDateStr = dayjs(dateValue.evaDate).format('YYYY-MM-DD');
      const pointRows = (steps || []).map((s, idx) => ({
        sdCode: newSdCode,
        value: pagePoint.current[`no${idx + 1}`] ?? 0,
        codeNm: s.label,
        custCd,
        evaDate: evaDateStr,
      }));
      const res3 = await SavePointAsync(pointRows, { userInfo });
      const d3 = Array.isArray(res3?.data) ? res3.data : [];

      // requestSave의 반환값은 useSaveN 내부에서 스낵바(detailData)에 사용됨
      // - useSaveN은 isSuccess 기반으로 성공/실패 집계하므로, detailData는 isSuccess 포함 형태로 통일
      const merged = [
        {
          isSuccess: true,
          statusType: res1?.statusType || res1?.StatusType,
          message: res1?.message || res1?.Message,
          sdCode: newSdCode,
          type: 'MASTER',
        },
        ...d2,
        ...d3,
      ];

      // 신규 저장 후에는 목록으로 이동(기존 UX 유지)
      if (wasNew) {
        navigate('/SeriousAccidentAct');
      } else {
        SearchLogic1(newSdCode);
      }

      // 저장 성공 후 원본 스냅샷 갱신(조회 merge용)
      arrayList2OriginRef.current = (arrayList2 || []).map((r) => ({ ...r }));

      return { ...res1, severity: 'success', data: merged };
    },
  });

  const SaveLogic1 = async () => {
    if (isTemplateMode) {
      await SaveTemplateLogic();
      return;
    }
    await SaveAll();
  };
  //#endregion
  //#region 구조 편집 (대분류 · 평가지표 · 착안사항)
  /** 대분류 추가 */
  const AddCategoryLogic = () => {
    const newIndex = steps.length;
    setSteps((prev) => [
      ...prev,
      {
        id: CreateTempCode('cat'),
        label: `신규 대분류 ${prev.length + 1}`,
      },
    ]);
    setActiveStep(newIndex);
    dispatch(
      showSnackbar({
        message: '대분류가 추가되었습니다. 이름을 입력해 주세요.',
        type: 'info',
      })
    );
    return newIndex;
  };

  /** 대분류명 수정 */
  const UpdateCategoryLogic = (categoryIndex, label) => {
    const next = String(label ?? '').trim();
    if (!next) return false;
    setSteps((prev) =>
      prev.map((row, i) => (i === categoryIndex ? { ...row, label: next } : row))
    );
    return true;
  };

  /** 대분류 삭제 */
  const DeleteCategoryLogic = (categoryIndex) => {
    if (steps.length <= 1) {
      dispatch(
        showSnackbar({
          message: '최소 1개의 대분류는 유지해야 합니다.',
          type: 'warning',
        })
      );
      return false;
    }

    const deletedVal1 = String(categoryIndex + 1);
    const removedCodes = arrayList
      .filter((r) => String(r.val1) === deletedVal1)
      .map((r) => r.code);

    const nextList2 = arrayList2
      .filter((r) => !removedCodes.includes(r.groupCd))
      .map((r) => {
        const v = Number(r.val1);
        return v > Number(deletedVal1) ? { ...r, val1: String(v - 1) } : r;
      });

    const nextList = arrayList
      .filter((r) => String(r.val1) !== deletedVal1)
      .map((r) => {
        const v = Number(r.val1);
        return v > Number(deletedVal1) ? { ...r, val1: String(v - 1) } : r;
      });

    const nextSteps = steps.filter((_, i) => i !== categoryIndex);
    setSteps(nextSteps);
    setActiveStep((prev) => Math.min(prev, Math.max(0, nextSteps.length - 1)));
    SyncScores(nextList, nextList2, nextSteps.length);
    dispatch(
      showSnackbar({ message: '대분류가 삭제되었습니다.', type: 'success' })
    );
    return true;
  };

  /** 평가지표 추가 */
  const AddIndicatorLogic = (stepIndex = activeStep) => {
    const val1 = String(stepIndex + 1);
    const code = CreateTempCode('ind');
    const newIndicator = {
      code,
      codeNm: '신규 평가지표',
      val1,
      val2: 0,
      val3: '0.00',
    };
    const newCheckpoint = {
      code: CreateTempCode('cp'),
      groupCd: code,
      codeNm: '착안사항을 입력하세요',
      check: 'N',
      val1,
    };
    SyncScores([...arrayList, newIndicator], [...arrayList2, newCheckpoint]);
    return code;
  };

  /** 평가지표명 수정 */
  const UpdateIndicatorLogic = (indicatorCode, codeNm) => {
    const next = String(codeNm ?? '').trim();
    if (!next) return false;
    setArrayList((prev) =>
      prev.map((row) =>
        row.code === indicatorCode ? { ...row, codeNm: next } : row
      )
    );
    return true;
  };

  /** 평가지표 삭제 */
  const DeleteIndicatorLogic = (indicator) => {
    if (!indicator?.code) return false;
    const nextList = arrayList.filter((r) => r.code !== indicator.code);
    const nextList2 = arrayList2.filter((r) => r.groupCd !== indicator.code);
    SyncScores(nextList, nextList2);
    dispatch(
      showSnackbar({ message: '평가지표가 삭제되었습니다.', type: 'success' })
    );
    return true;
  };

  /** 착안사항 추가 */
  const AddCheckpointLogic = (indicator) => {
    if (!indicator?.code) return null;
    const val1 = String(indicator.val1);
    const newCheckpoint = {
      code: CreateTempCode('cp'),
      groupCd: indicator.code,
      codeNm: '신규 착안사항',
      check: 'N',
      val1,
    };
    SyncScores(arrayList, [...arrayList2, newCheckpoint]);
    return newCheckpoint.code;
  };

  /** 착안사항명 수정 */
  const UpdateCheckpointLogic = (checkpointCode, codeNm) => {
    const next = String(codeNm ?? '').trim();
    if (!next) return false;
    setArrayList2((prev) =>
      prev.map((row) =>
        row.code === checkpointCode ? { ...row, codeNm: next } : row
      )
    );
    return true;
  };

  /** 평가지표 배점(소계) 수정 — 착안사항은 개수만큼 균등 분배 */
  const UpdateIndicatorPointLogic = (indicatorCode, rawPoint) => {
    const val2 = Math.max(0, Number(rawPoint) || 0);
    const target = arrayList.find((row) => row.code === indicatorCode);
    if (!target) return false;

    const categoryKey = String(target.val1);
    const otherSum = arrayList
      .filter(
        (row) => String(row.val1) === categoryKey && row.code !== indicatorCode
      )
      .reduce((sum, row) => sum + Number(row.val2 || 0), 0);
    const categoryTotal = Number((otherSum + val2).toFixed(2));

    if (categoryTotal > CATEGORY_MAX_SCORE) {
      dispatch(
        showSnackbar({
          message: `대분류 총배점은 ${CATEGORY_MAX_SCORE}점을 초과할 수 없습니다. (현재 ${otherSum}점 + ${val2}점 = ${categoryTotal}점)`,
          type: 'warning',
        })
      );
      return false;
    }

    const nextList = arrayList.map((row) =>
      row.code === indicatorCode ? { ...row, val2 } : row
    );
    SyncScores(nextList, arrayList2);
    return true;
  };

  /** 착안사항 삭제 */
  const DeleteCheckpointLogic = (checkpoint, indicator) => {
    const siblings = arrayList2.filter((r) => r.groupCd === checkpoint.groupCd);
    if (siblings.length <= 1) {
      dispatch(
        showSnackbar({
          message: '평가지표당 최소 1개의 착안사항이 필요합니다.',
          type: 'warning',
        })
      );
      return false;
    }
    const nextList2 = arrayList2.filter((r) => r.code !== checkpoint.code);
    SyncScores(arrayList, nextList2);
    dispatch(
      showSnackbar({ message: '착안사항이 삭제되었습니다.', type: 'success' })
    );
    return true;
  };
  //#endregion
  //#region 나머지 함수
  const HandleNext = () => {
    // if (maxSteps === activeStep + 1) {
    //   SaveLogic1();
    // } else {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // }
  };

  const HandleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  //페이지별 취득점수
  const ChangePagePoint = (val1, value, mode) => {
    const key = `no${val1}`;
    const current = Number(pagePoint.current[key] || 0);
    pagePoint.current[key] =
      mode === 'add' ? current + value : current - value;
  };

  // 라디오버튼 클릭시 이벤트 (_value1: 착안사항, _value2: Y|N|0)
  const HandleCheckboxChange = (_value1, _value2) => {
    const nextList2 = arrayList2.map((item) =>
      item.code === _value1.code ? { ...item, check: _value2 } : item
    );
    SyncScores(arrayList, nextList2);
  };

  /** 경영자 안전보건경영활동(1단계) 엑셀 다운로드 */
  const ExcelDownload1 = async () => {
    try {
      dispatch(showLoading());

      const stepRows = arrayList.filter((e) => String(e.val1) === '1');
      if (!stepRows.length) {
        dispatch(
          showSnackbar({
            message: '다운로드할 평가 항목이 없습니다. 조회 후 다시 시도해 주세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const ctx = {
        steps,
        arrayList,
        arrayList2,
        dateValue,
        totalPoint,
        custNm: custNmForExcel,
      };
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet(
        SanitizeExcelSheetName(steps[0]?.label || '1단계')
      );
      WriteSeriousAccidentStepToWorksheet(ws, '1', ctx);

      const dateTimeStr = dayjs().format('YYYYMMDD_HHmmss');
      await DownloadSeriousAccidentWorkbook(
        workbook,
        `경영자_안전보건경영활동_${dateTimeStr}.xlsx`
      );

      dispatch(
        showSnackbar({
          message: '엑셀 다운로드가 완료되었습니다.',
          type: 'success',
        })
      );
      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel download error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error?.message || ''}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  /** Steps 전체(경영자~가점) 단계별 시트 일괄 엑셀 다운로드 */
  const ExcelDownloadAll = async () => {
    try {
      dispatch(showLoading());

      if (!arrayList.length) {
        dispatch(
          showSnackbar({
            message: '다운로드할 평가 데이터가 없습니다. 조회 후 다시 시도해 주세요.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      const ok = await DownloadSeriousAccidentExcelAll({
        steps,
        arrayList,
        arrayList2,
        dateValue,
        totalPoint,
        custNm: custNmForExcel,
      });

      if (!ok) {
        dispatch(
          showSnackbar({
            message: '다운로드할 평가 데이터가 없습니다.',
            type: 'warning',
          })
        );
        dispatch(closeLoading());
        return;
      }

      dispatch(
        showSnackbar({
          message: `전체 ${steps.length}개 단계 엑셀 다운로드가 완료되었습니다.`,
          type: 'success',
        })
      );
      dispatch(closeLoading());
    } catch (error) {
      console.error('Excel bulk download error:', error);
      dispatch(
        showSnackbar({
          message: `일괄 엑셀 다운로드 중 오류가 발생했습니다: ${error?.message || ''}`,
          type: 'error',
        })
      );
      dispatch(closeLoading());
    }
  };

  //#endregion

  return (
    <div className="wrap">
      <SER_BASIC_01
        userInfo={userInfo}
        dateValue={dateValue}
        setDateValue={setDateValue}
        lanList={lanInfo}
        HandleNext={HandleNext}
        HandleBack={HandleBack}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        codeList={codeList}
        setCodeList={setCodeList}
        arrayList={arrayList}
        setArrayList={setArrayList}
        arrayList2={arrayList2}
        setArrayList2={setArrayList2}
        HandleCheckboxChange={HandleCheckboxChange}
        totalPoint={totalPoint}
        setTotalPoint={setTotalPoint}
        steps={steps}
        setSteps={setSteps}
        maxSteps={maxSteps}
        navigate={navigate}
        AddCategoryLogic={AddCategoryLogic}
        UpdateCategoryLogic={UpdateCategoryLogic}
        DeleteCategoryLogic={DeleteCategoryLogic}
        AddIndicatorLogic={AddIndicatorLogic}
        UpdateIndicatorLogic={UpdateIndicatorLogic}
        DeleteIndicatorLogic={DeleteIndicatorLogic}
        UpdateIndicatorPointLogic={UpdateIndicatorPointLogic}
        AddCheckpointLogic={AddCheckpointLogic}
        UpdateCheckpointLogic={UpdateCheckpointLogic}
        DeleteCheckpointLogic={DeleteCheckpointLogic}
        // 함수
        SearchLogic1={() => SearchLogic1(sdCodeRef.current)}
        SaveLogic1={SaveLogic1}
        ExcelDownload1={ExcelDownload1}
        ExcelDownloadAll={ExcelDownloadAll}
        custNm={custNmForExcel}
        screenMode={screenMode}
        isTemplateMode={isTemplateMode}
        readOnlyStructure={readOnlyStructure}
        formName={formNameRef.current}
        formVer={formVerRef.current}
        formStatus={formStatusRef.current}
        templateReadOnly={templateReadOnlyRef.current}
        isNewForm={isNewFormRef.current}
        PublishTemplateLogic={PublishTemplateLogic}
      />
    </div>
  );
}

export default SER_BASIC_01F;
